import JSZip from 'jszip';
import { GCodeFile } from '../components/GCodePreviewCard';

/**
 * Extract GCode files from a ZIP archive URL
 * This function is DEPRECATED and only kept for backward compatibility with older projects 
 * that still use the ZIP-based approach. New projects save gcode files directly in timestamped folders.
 * 
 * @param zipUrl URL of the ZIP file containing GCode files
 * @param projectName Name of the project (used for generating GCode file IDs)
 * @returns Promise resolving to an array of GCodeFile objects
 * @deprecated Use direct gcode file access instead
 */
export async function extractGCodeFiles(zipUrl: string, projectName: string): Promise<GCodeFile[]> {
  try {
    console.log(`[DEBUG] Extracting GCode files from ZIP: ${zipUrl} for project: ${projectName}`);
    
    // Fetch the ZIP file
    const response = await fetch(zipUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch ZIP file: ${response.statusText}`);
    }
    
    // Parse the ZIP content
    const zipBlob = await response.blob();
    console.log(`[DEBUG] Received ZIP blob of size: ${zipBlob.size} bytes`);
    
    const zip = new JSZip();
    const zipContents = await zip.loadAsync(zipBlob);
    
    // Log all files in the ZIP for debugging
    console.log(`[DEBUG] ZIP contents:`, Object.keys(zipContents.files));
    
    // Filter for GCode files and extract their data
    const gcodeFiles: GCodeFile[] = [];
    
    for (const [filename, file] of Object.entries(zipContents.files)) {
      // Skip directories and non-gcode files
      if (file.dir || !filename.toLowerCase().endsWith('.gcode')) {
        continue;
      }
      
      console.log(`[DEBUG] Processing GCode file from ZIP: ${filename}`);
      
      // Extract plate number from filename
      const plateNumberMatch = filename.match(/plate_(\d+)/i);
      const plateNumber = plateNumberMatch ? parseInt(plateNumberMatch[1], 10) : 0;
      
      // Get file size
      const content = await file.async('blob');
      const size = content.size;
      
      // Create a unique ID for this GCode file
      const id = `gcode_${projectName.replace(/\s+/g, '_')}_${filename.replace(/\s+/g, '_')}_${Date.now()}`;
      
      // Create a data URL for content (to be compatible with new direct approach)
      const fileReader = new FileReader();
      const contentUrl = await new Promise<string>((resolve) => {
        fileReader.onload = () => resolve(fileReader.result as string);
        fileReader.readAsDataURL(content);
      });
      
      gcodeFiles.push({
        id,
        filename,
        size,
        plateNumber,
        download_url: contentUrl,
      });
      
      console.log(`[DEBUG] Added GCode file: ${filename}, size: ${size}, plate: ${plateNumber}`);
    }
    
    if (gcodeFiles.length === 0) {
      console.warn(`[DEBUG] No GCode files found in ZIP archive for project: ${projectName}`);
    } else {
      console.log(`[DEBUG] Found ${gcodeFiles.length} GCode files in ZIP archive for project: ${projectName}`);
    }
    
    // Sort by plate number
    return gcodeFiles.sort((a, b) => a.plateNumber - b.plateNumber);
  } catch (error) {
    console.error('[DEBUG] Error extracting GCode files:', error);
    throw error;
  }
}

/**
 * Format file size into human-readable format
 * 
 * @param bytes File size in bytes
 * @returns Formatted size string (e.g., "1.23 MB")
 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return bytes + ' B';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + ' KB';
  } else if (bytes < 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  } else {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }
}

// Format time from seconds to hours, minutes, seconds
export function formatPrintTime(seconds: number): string {
  // Return N/A for invalid values
  if (seconds === null || seconds === undefined || seconds < 0) return 'N/A';
  if (seconds === 0) return '0s';
  
  const days = Math.floor(seconds / 86400); // 86400 seconds in a day
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  if (days > 0) {
    // For day+ prints, show days, hours, and minutes (like PrusaSlicer: "1d 8h 30m")
    let result = `${days}d`;
    if (hours > 0) {
      result += ` ${hours}h`;
    }
    if (minutes > 0) {
      result += ` ${minutes}m`;
    }
    return result;
  } else if (hours > 0) {
    // For hour+ prints, show hours and minutes
    return `${hours}h ${minutes > 0 ? minutes + 'm' : ''}`;
  } else if (minutes > 0) {
    // For minute+ prints, show minutes and seconds
    return `${minutes}m${remainingSeconds > 0 ? ' ' + remainingSeconds + 's' : ''}`;
  } else {
    // For seconds-only prints
    return `${remainingSeconds}s`;
  }
}

// Extract print information from G-code
export function extractGCodeInfo(gcodeContent: string): { 
  estimatedTime: number | null;
  filamentLength: number | null;
  filamentWeight: number | null;
  layerCount: number | null;
  printerInfo: string | null;
  nozzleDiameter: number | null;
  materialType: string | null;
} {
  if (!gcodeContent) {
    return {
      estimatedTime: null,
      filamentLength: null,
      filamentWeight: null,
      layerCount: null,
      printerInfo: null,
      nozzleDiameter: null,
      materialType: null
    };
  }
  
  console.log('[DEBUG] Starting G-code info extraction...');
  
  // Results object
  const result = {
    estimatedTime: null as number | null,
    filamentLength: null as number | null,
    filamentWeight: null as number | null,
    layerCount: null as number | null,
    printerInfo: null as string | null,
    nozzleDiameter: null as number | null,
    materialType: null as string | null
  };
  
  // Common patterns in G-code files from different slicers
  const timePatterns = [
    // MINUTES-ONLY patterns FIRST - highest priority for short prints
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)m\s+(\d+)s(?!\w)/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)m(?!\w)/i,
    /; Time elapsed:\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /; Time elapsed:\s+(\d+)m(?!\w)/i,
    /;Print Time: (\d+)m\s+(\d+)s(?!\w)/i,
    /;Print Time: (\d+)m(?!\w)/i,
    /;PRINT TIME: (\d+)m\s+(\d+)s(?!\w)/i,
    /;PRINT TIME: (\d+)m(?!\w)/i,
    /;Estimated printing time:.+(\d+)m\s+(\d+)s(?!\w)/i,
    /;Estimated printing time:.+(\d+)m(?!\w)/i,
    // HOUR patterns SECOND - very specific, must NOT contain 'd' anywhere in line
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)h\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)h\s+(\d+)m(?!\w)/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)h(?!\w)/i,
    /; Time elapsed:\s+(\d+)h\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /; Time elapsed:\s+(\d+)h\s+(\d+)m(?!\w)/i,
    /; Time elapsed:\s+(\d+)h(?!\w)/i,
    /;Print Time: (\d+)h\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /;Print Time: (\d+)h\s+(\d+)m(?!\w)/i,
    /;Print Time: (\d+)h(?!\w)/i,
    /;PRINT TIME: (\d+)h\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /;PRINT TIME: (\d+)h\s+(\d+)m(?!\w)/i,
    /;PRINT TIME: (\d+)h(?!\w)/i,
    /;Estimated printing time:.+(\d+)h\s+(\d+)m\s+(\d+)s(?!\w)/i,
    /;Estimated printing time:.+(\d+)h\s+(\d+)m(?!\w)/i,
    /;Estimated printing time:.+(\d+)h(?!\w)/i,
    // Time in HH:MM:SS format
    /; total print time = (\d+):(\d+):(\d+)/i,
    /;Estimated printing time \(.*\) = (\d+):(\d+):(\d+)/i,
    // Time in seconds only
    /;TIME:(\d+)/i,
    // DAY patterns LAST - only when line contains 'd'
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)d\s+(\d+)h\s+(\d+)m\s+(\d+)s/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)d\s+(\d+)h\s+(\d+)m/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)d\s+(\d+)h/i,
    /; estimated printing time(?:\s+\(normal mode\))?\s*=\s*(\d+)d/i,
    /;Estimated printing time:.+(\d+)d\s+(\d+)h\s+(\d+)m\s+(\d+)s/i,
    /;Estimated printing time:.+(\d+)d\s+(\d+)h\s+(\d+)m/i,
    /;Estimated printing time:.+(\d+)d\s+(\d+)h/i,
    /;Estimated printing time:.+(\d+)d/i
  ];
  
  const filamentPatterns = [
    /; filament used(?:\s+\[mm\])?\s*=\s*([\d.]+)/i, // Length in mm
    /; filament used(?:\s+\[g\])?\s*=\s*([\d.]+)/i,  // Weight in g
    /; filament used(?:\s+\[cm3\])?\s*=\s*([\d.]+)/i, // Volume in cm3
    /;Filament used: ([\d.]+)m/i, // Length in meters
    /;Filament weight: ([\d.]+)g/i, // Weight in grams
    /;FILAMENT_USED: ([\d.]+)/i, // Filament in mm
    /;Material weight: ([\d.]+) g/i, // PrusaSlicer format for weight
    /;Filament length: ([\d.]+) mm/i, // PrusaSlicer format for length
    /;Total filament used \[g\] = ([\d.]+)/i, // PrusaSlicer newer format
    /;Total filament cost = .+/i
  ];
  
  const layerPatterns = [
    /; layer_count = (\d+)/i,
    /; layer count: (\d+)/i,
    /;LAYER_COUNT:(\d+)/i,
    /;Layer count: (\d+)/i,
    /;Layers count: (\d+)/i
  ];
  
  // Patterns to identify printer information
  const printerPatterns = [
    /; printer_name\s*=\s*(.+)/i,
    /; printer_model\s*=\s*(.+)/i,
    /; printer\s*=\s*(.+)/i,
    /;PRINTER_TYPE:(.+)/i,
    /;Printer: (.+)/i,
    /;Printer Brand: (.+)/i,
    /;Printer Model: (.+)/i,
    /;Printer Name: (.+)/i,
    /;printer_settings_id = (.+)/i, // PrusaSlicer format
    /;machine_name = (.+)/i, // Cura format
    /;Machine Name: (.+)/i  // Another Cura format
  ];

  // Patterns to identify nozzle diameter
  const nozzlePatterns = [
    /; nozzle_diameter\s*=\s*([\d.]+)/i, // PrusaSlicer format
    /; nozzle diameter\s*[=:]\s*([\d.]+)/i,
    /;Nozzle diameter: ([\d.]+)/i,
    /;NOZZLE_DIAMETER:([\d.]+)/i,
    /;machine_nozzle_size = ([\d.]+)/i, // Cura format
    /;Nozzle size: ([\d.]+)/i,
    /;nozzle size = ([\d.]+)/i,
    /;Extruder.*diameter.*?(\d+\.?\d*)/i, // Generic extruder diameter
    /;Line width.*?(\d+\.?\d*)/i // Sometimes nozzle size is indicated by line width
  ];

  // Patterns to identify material/filament type
  const materialPatterns = [
    /; filament_type\s*=\s*(.+)/i, // PrusaSlicer format
    /; filament type\s*[=:]\s*(.+)/i,
    /;Filament type: (.+)/i,
    /;FILAMENT_TYPE:(.+)/i,
    /;Material: (.+)/i,
    /;material = (.+)/i, // Cura format
    /;Material Name: (.+)/i,
    /;filament_settings_id = (.+)/i, // PrusaSlicer settings ID often contains material
    /;Filament brand: (.+)/i,
    /;Brand: (.+)/i,
    /;PLA|ABS|PETG|TPU|ASA|PC|Nylon|Wood|Carbon/i, // Direct material names
    /;filament.*?PLA|ABS|PETG|TPU|ASA|PC|Nylon|Wood|Carbon/i // Material names in filament lines
  ];
  
  // Split into lines for parsing
  const lines = gcodeContent.split('\n');
  
  // For more thorough search, we'll check both header and footer
  // Looking at more lines because some slicers put metadata deep in the file
  const headerLines = lines.slice(0, 500);  // Look at first 500 lines
  const footerLines = lines.slice(-500);    // Look at last 500 lines
  let searchLines = [...headerLines, ...footerLines];
  
  // First pass with focused search in header/footer
  searchForMetadata(searchLines);
  
  // If we didn't find all metadata, do a second pass with ALL lines
  // This is more expensive but will catch metadata anywhere in the file
  if (result.estimatedTime === null || result.filamentWeight === null || result.layerCount === null || 
      result.printerInfo === null || result.nozzleDiameter === null || result.materialType === null) {
    console.log('[DEBUG] Metadata not found in header/footer, searching entire file...');
    searchLines = lines;
    searchForMetadata(searchLines);
  }
  
  // Final fallback estimates for missing metadata
  applyFallbackEstimates();
  
  console.log('[DEBUG] Completed G-code metadata extraction:', result);
  return result;
  
  // Helper function to search for metadata in lines
  function searchForMetadata(lines: string[]) {
    // Search for time estimate
    if (result.estimatedTime === null) {
      for (const line of lines) {
        if (line.toLowerCase().includes('time') || line.toLowerCase().includes('duration')) {
          console.log('[DEBUG] Found time info line:', line);
        }
        
        for (const pattern of timePatterns) {
          const match = line.match(pattern);
          if (match) {
            console.log('[DEBUG] Pattern matched:', pattern.toString(), 'on line:', line);
            console.log('[DEBUG] Match result:', match);
            console.log('[DEBUG] Raw estimated time calculation will be:');
            
            // SUPER strict day check - line must contain 'd', pattern must contain 'd', and line must NOT be a simple hour/minute format
            if (line.toLowerCase().includes('d') && pattern.toString().includes('d') && !line.match(/^\s*;\s*estimated printing time.*=\s*\d+[hm]\s+\d+[ms]/i)) {
              // Handle time in days format (PrusaSlicer format like "1d 3h 10m 46s")
              const days = match[1] ? parseInt(match[1], 10) : 0;
              const hours = match[2] ? parseInt(match[2], 10) : 0;
              const minutes = match[3] ? parseInt(match[3], 10) : 0;
              const seconds = match[4] ? parseInt(match[4], 10) : 0; // Added seconds back
              result.estimatedTime = days * 86400 + hours * 3600 + minutes * 60 + seconds;
              console.log('[DEBUG] Found print time (d h m s):', days, 'd', hours, 'h', minutes, 'm', seconds, 's');
              console.log('[DEBUG] Total seconds calculated:', result.estimatedTime);
              break;
            } else if (pattern.toString().includes(':')) {
              // Handle time in HH:MM:SS format
              if (match[1] && match[2] && match[3]) {
                const hours = parseInt(match[1], 10);
                const minutes = parseInt(match[2], 10);
                const seconds = parseInt(match[3], 10);
                result.estimatedTime = hours * 3600 + minutes * 60 + seconds;
                console.log('[DEBUG] Found print time (HH:MM:SS):', hours, 'h', minutes, 'm', seconds, 's');
                console.log('[DEBUG] Total seconds calculated:', result.estimatedTime);
                break;
              }
            } else if (pattern.toString().includes('TIME:')) {
              // Handle time in seconds only (like Simplify3D)
              result.estimatedTime = parseInt(match[1], 10);
              console.log('[DEBUG] Found print time (seconds):', result.estimatedTime);
              break;
            } else if (pattern.toString().includes('h') && !line.toLowerCase().includes('d')) {
              // Handle hour/minute/second format (only if line doesn't contain 'd')
              const hours = match[1] ? parseInt(match[1], 10) : 0;
              const minutes = match[2] ? parseInt(match[2], 10) : 0;
              const seconds = match[3] ? parseInt(match[3], 10) : 0;
              result.estimatedTime = hours * 3600 + minutes * 60 + seconds;
              console.log('[DEBUG] Found print time (h m s):', hours, 'h', minutes, 'm', seconds, 's');
              console.log('[DEBUG] Total seconds calculated:', result.estimatedTime);
              break;
            } else if (pattern.toString().includes('m') && !line.toLowerCase().includes('d') && !pattern.toString().includes('h')) {
              // Handle minutes/seconds only format (only if line doesn't contain 'd' or 'h')
              const minutes = match[1] ? parseInt(match[1], 10) : 0;
              const seconds = match[2] ? parseInt(match[2], 10) : 0;
              result.estimatedTime = minutes * 60 + seconds;
              console.log('[DEBUG] Found print time (m s):', minutes, 'm', seconds, 's');
              console.log('[DEBUG] Total seconds calculated:', result.estimatedTime);
              break;
            } else {
              // Fallback for any other format
              console.log('[DEBUG] Unhandled pattern, skipping...');
            }
          }
        }
        
        if (result.estimatedTime !== null) break;
      }
    }
    
    // Search for filament usage
    let foundFilamentInfo = false;
    for (const line of lines) {
      // Add debug logging for every filament-related comment
      if ((line.toLowerCase().includes('filament') || 
           line.toLowerCase().includes('material')) && 
          !foundFilamentInfo) {
        console.log('[DEBUG] Found filament info line:', line);
        foundFilamentInfo = true; // Only log the first few filament lines
      }
      
      // Look for filament length first
      if (result.filamentLength === null) {
        for (const pattern of filamentPatterns) {
          if (pattern.toString().includes('mm')) {
            const match = line.match(pattern);
            if (match && match[1]) {
              result.filamentLength = parseFloat(match[1]);
              console.log('[DEBUG] Found filament length:', result.filamentLength, 'mm');
              break;
            }
          }
          
          // Convert meters to mm
          if (pattern.toString().includes('m') && !pattern.toString().includes('mm')) {
            const match = line.match(pattern);
            if (match && match[1]) {
              result.filamentLength = parseFloat(match[1]) * 1000; // Convert meters to mm
              console.log('[DEBUG] Found filament length (converted from m):', result.filamentLength, 'mm');
              break;
            }
          }
        }
      }
      
      // Look for filament weight
      if (result.filamentWeight === null) {
        for (const pattern of filamentPatterns) {
          if (pattern.toString().includes('g') && !pattern.toString().includes('cg')) {
            const match = line.match(pattern);
            if (match && match[1]) {
              result.filamentWeight = parseFloat(match[1]);
              console.log('[DEBUG] Found filament weight:', result.filamentWeight, 'g');
              break;
            }
          }
        }
        
        // Also try extracting any digit followed by 'g' for weight
        if (result.filamentWeight === null) {
          const genericWeightMatch = line.match(/[=:]\s*([\d.]+)\s*g\b/i);
          if (genericWeightMatch && genericWeightMatch[1] && 
              (line.toLowerCase().includes('filament') || line.toLowerCase().includes('material'))) {
            result.filamentWeight = parseFloat(genericWeightMatch[1]);
            console.log('[DEBUG] Found filament weight with generic pattern:', result.filamentWeight, 'g');
          }
        }
      }
      
      // If we found both, break
      if (result.filamentLength !== null && result.filamentWeight !== null) break;
    }
    
    // Search for layer count
    if (result.layerCount === null) {
      for (const line of lines) {
        if (line.toLowerCase().includes('layer') && line.toLowerCase().includes('count')) {
          console.log('[DEBUG] Found layer count info line:', line);
        }
        
        for (const pattern of layerPatterns) {
          const match = line.match(pattern);
          if (match && match[1]) {
            result.layerCount = parseInt(match[1], 10);
            console.log('[DEBUG] Found layer count:', result.layerCount);
            break;
          }
        }
        
        if (result.layerCount !== null) break;
      }
    }
    
    // Search for printer information
    if (result.printerInfo === null) {
      for (const line of lines) {
        if (line.toLowerCase().includes('printer') || line.toLowerCase().includes('machine')) {
          console.log('[DEBUG] Found printer info line:', line);
          
          for (const pattern of printerPatterns) {
            const match = line.match(pattern);
            if (match && match[1]) {
              // Clean up the result, removing quotes and extra whitespace
              result.printerInfo = match[1].trim().replace(/["']/g, '');
              console.log('[DEBUG] Found printer info:', result.printerInfo);
              break;
            }
          }
          
          if (result.printerInfo !== null) break;
        }
      }
    }

    // Search for nozzle diameter
    if (result.nozzleDiameter === null) {
      for (const line of lines) {
        if (line.toLowerCase().includes('nozzle') || line.toLowerCase().includes('extruder')) {
          console.log('[DEBUG] Found nozzle info line:', line);
          
          for (const pattern of nozzlePatterns) {
            const match = line.match(pattern);
            if (match && match[1]) {
              result.nozzleDiameter = parseFloat(match[1]);
              console.log('[DEBUG] Found nozzle diameter:', result.nozzleDiameter);
              break;
            }
          }
          
          if (result.nozzleDiameter !== null) break;
        }
      }
    }

    // Search for material type
    if (result.materialType === null) {
      for (const line of lines) {
        if (line.toLowerCase().includes('filament') || line.toLowerCase().includes('material')) {
          console.log('[DEBUG] Found material info line:', line);
          
          for (const pattern of materialPatterns) {
            const match = line.match(pattern);
            if (match && match[1]) {
              result.materialType = match[1].trim();
              console.log('[DEBUG] Found material type:', result.materialType);
              break;
            }
          }
          
          if (result.materialType !== null) break;
        }
      }
    }
  }
  
  // Apply fallback estimates for missing data
  function applyFallbackEstimates() {
    // If we still have no print time, look for any numeric time pattern in comments
    if (result.estimatedTime === null) {
      // Search for any number followed by 'm' (minutes) or 's' (seconds)
      for (const line of searchLines) {
        if (line.toLowerCase().includes('time') || line.toLowerCase().includes('duration')) {
          // Try to extract minutes
          const minutesMatch = line.match(/(\d+)\s*m/i);
          if (minutesMatch && minutesMatch[1]) {
            const minutes = parseInt(minutesMatch[1], 10);
            result.estimatedTime = minutes * 60;
            console.log('[DEBUG] Fallback time estimation (minutes only):', minutes, 'm =', result.estimatedTime, 's');
            break;
          }
          
          // If no minutes, try to extract raw seconds
          const secondsMatch = line.match(/(\d+)\s*s/i);
          if (secondsMatch && secondsMatch[1]) {
            result.estimatedTime = parseInt(secondsMatch[1], 10);
            console.log('[DEBUG] Fallback time estimation (seconds only):', result.estimatedTime, 's');
            break;
          }
        }
      }
      
      // Last resort: If filament length is known, make very rough estimate
      if (result.estimatedTime === null && result.filamentLength !== null) {
        // Assuming average extrusion rate of 5mm per second
        const estimatedSeconds = result.filamentLength / 5;
        result.estimatedTime = Math.round(estimatedSeconds);
        console.log('[DEBUG] Last resort time estimation from filament length:', result.estimatedTime, 's');
      }
    }
    
    // If we only have volume but not weight, estimate weight
    // PLA density is approximately 1.24 g/cm³
    if (result.filamentWeight === null) {
      // Look for volume in cm³
      for (const line of searchLines) {
        if (line.toLowerCase().includes('cm3') || line.toLowerCase().includes('cm³')) {
          const volumeMatch = line.match(/[=:]\s*([\d.]+)\s*(?:cm3|cm³)/i);
          if (volumeMatch && volumeMatch[1]) {
            const volume = parseFloat(volumeMatch[1]);
            // Estimate weight using PLA density
            result.filamentWeight = volume * 1.24;
            console.log('[DEBUG] Estimated weight from volume:', result.filamentWeight, 'g (from', volume, 'cm³)');
            break;
          }
        }
      }
    }
    
    // If we have length but not weight, estimate weight
    // For 1.75mm PLA filament, approx 3g per meter
    if (result.filamentWeight === null && result.filamentLength !== null) {
      // Convert mm to meters and calculate weight
      const filamentLengthMeters = result.filamentLength / 1000;
      result.filamentWeight = filamentLengthMeters * 3;
      console.log('[DEBUG] Estimated weight from length:', result.filamentWeight, 'g (from', filamentLengthMeters, 'm)');
    }
    
    // If we didn't find the layer count, try to count them from layer change comments
    if (result.layerCount === null) {
      // Count unique layer numbers from layer change comments
      const layerNumbers = new Set<number>();
      const layerPattern = /; layer (\d+)/i;
      
      for (const line of lines) {
        const match = line.match(layerPattern);
        if (match && match[1]) {
          layerNumbers.add(parseInt(match[1], 10));
        }
      }
      
      if (layerNumbers.size > 0) {
        result.layerCount = Math.max(...layerNumbers) + 1; // +1 because layers usually start from 0
        console.log('[DEBUG] Counted layer numbers from comments:', result.layerCount);
      }
    }
    
    // Last attempt to find printer info
    if (result.printerInfo === null) {
      // Look for any line with printer information
      for (const line of searchLines) {
        if (line.toLowerCase().includes('printer') || line.toLowerCase().includes('machine')) {
          const genericMatch = line.match(/;.*?(?:printer|machine)[^:=]*[=:]\s*([^;]+)/i);
          if (genericMatch && genericMatch[1] && genericMatch[1].trim()) {
            result.printerInfo = genericMatch[1].trim();
            console.log('[DEBUG] Found printer info with generic pattern:', result.printerInfo);
            break;
          }
        }
      }
    }
  }
} 